// A connectome or tissue pack's catalogue (connectome-pack-v0, such as H01's cortex): its neuropils
// (cortical layers, vessels, the tissue outline) and the cells the view is about, named from the
// pack's own tables, for the layer list, the inspector, search and the soma labels. The viewport
// loads the geometry itself. Ids are the viewport's: `neuropil:<id>` and `neuron:<body id>`.

import { index, type Catalogue, type PackInfo, type Provenance, type Structure } from './catalogue.ts';
import { loadActivity, type RunInfo } from './activity.ts';

export const CONNECTOME_CONTRACT = 'connectome-pack-v0';

/** One `neuropils.json` row. */
export interface NeuropilRow {
  index: number;
  id: string;
  name: string;
  hemisphere?: 'L' | 'R' | 'midline' | 'bilateral' | null;
  parent: string | null;
  /** Tissue packs: `shell` (the outline), `layer` (a cortical layer), `vessel`. */
  kind?: string;
  mesh?: string | null;
  layer?: string;
  provenance: Provenance;
  color?: string;
  source?: string;
}

/** `celltypes.json`: the types are contiguous ranges of the neuron table (`first`, `count`). */
export interface CellTypes {
  superclasses: { id: string; name: string; color?: string }[];
  types: { index: number; name: string; superclass: string; class?: string | null; first: number; count: number; color?: string }[];
}

/** The neuron table columns the catalogue reads; a tissue pack's `layer`, `in_column` and `skeleton` are optional. */
export interface NeuronTable {
  bodyId: Uint32Array;
  flags: Uint8Array;
  /** Index into `pack.json` `enums.layer`. */
  layer?: Uint8Array;
  inColumn?: Uint8Array;
  /** 0 none, 1 a proofread SWC, 2 an automatic skeleton. */
  skeleton?: Uint8Array;
}

/** `neurons/flags.u8` bit 1: the neuron has a mesh (H01's 104 proofread cells). */
export const HAS_MESH = 2;

/** What a connectome catalogue keeps of the tables it read: where the pack is, its cell types and its neuron table. */
export interface TissueTables {
  /** The pack's directory, `/packs/<id>/`. */
  base: string;
  celltypes: CellTypes;
  table: NeuronTable;
  /** `neurons/index.json`'s columns, by name: the inspector reads the ones the catalogue doesn't. */
  files: Record<string, { file: string }>;
}

const TYPE_NAME: Record<string, string> = {
  pyramidal: 'Pyramidal cell',
  'spiny stellate': 'Spiny stellate cell',
  'spiny, atypical tree': 'Spiny cell, atypical tree',
  'untyped neuron': 'Neuron, untyped',
  'untyped cell': 'Cell, untyped',
};

/** A cell's name: its type as a noun, then its layer ("Pyramidal cell, L2"). */
export function cellName(type: string | undefined, layer: string | undefined): string {
  const noun = type ? (TYPE_NAME[type] ?? type.replace(/^./, (c) => c.toUpperCase())) : 'Cell';
  return layer && layer !== 'unknown' ? `${noun}, ${layer}` : noun;
}

export const isConnectome = (c: Pick<Catalogue, 'kind'> | null | undefined) => c?.kind === 'connectome';

/** The group label for the neuropils by what they are: H01's are its cortical layers and a vessel mesh. */
function neuropilLabel(kinds: Set<string>): string {
  if (kinds.has('layer') && kinds.has('vessel')) return 'Layers and vessels';
  if (kinds.has('layer')) return 'Cortical layers';
  if (kinds.has('vessel')) return 'Vessels';
  return 'Neuropils';
}

/**
 * The catalogue. The cells are the ones with meshes (proofread) and the column's members, so
 * every arbor the view draws has a name: first the proofread cells in the column, taken a layer at
 * a time from the pia down so the first few soma labels span the cortex, then the neuropils, the
 * other proofread cells and the rest of the column. `centre` (column.json's body) leads.
 */
export function tissueCatalogue(pack: PackInfo, neuropils: NeuropilRow[], celltypes: CellTypes, t: NeuronTable): Catalogue {
  const layers = pack.enums?.layer ?? [];
  const typeOf = new Uint16Array(t.bodyId.length).fill(0xffff);
  for (const ty of celltypes.types) typeOf.fill(ty.index, ty.first, Math.min(t.bodyId.length, ty.first + ty.count));
  const colour = new Map(celltypes.superclasses.map((c) => [c.id, c.color]));
  const neurons = pack.layers?.['neurons'];
  const cite = pack.attribution?.citation;
  const proofread: number[] = [];
  const column: number[] = [];
  for (let i = 0; i < t.bodyId.length; i++) {
    if (t.flags[i]! & HAS_MESH) proofread.push(i);
    else if (t.inColumn?.[i] === 1) column.push(i);
  }
  const cell = (i: number): Structure => {
    const ty = celltypes.types[typeOf[i]!];
    const layer = t.layer ? layers[t.layer[i]!] : undefined;
    const skeleton = t.skeleton?.[i] ?? (t.flags[i]! & HAS_MESH ? 1 : 0);
    const body = t.bodyId[i]!;
    const color = ty?.color ?? (ty ? colour.get(ty.superclass) : undefined);
    return {
      id: `neuron:${body}`,
      name: cellName(ty?.name, layer),
      synonyms: [`soma ${body}`, ...(ty ? [ty.name] : [])],
      system: 'neurons',
      parent: layer && neuropils.some((n) => n.id === layer) ? `neuropil:${layer}` : null,
      hemisphere: null,
      provenance: (neurons?.provenance as Provenance | undefined) ?? 'derived',
      description:
        skeleton === 1
          ? `Soma ${body}. One of the ${proofread.length} cells whose arbors were proofread by hand.`
          : skeleton === 2
            ? `Soma ${body}. Its arbor is the automatic segmentation’s.`
            : `Soma ${body}. Its soma only: no arbor was traced.`,
      ...(neurons?.method ? { method: neurons.method } : {}),
      ...(neurons?.confidence ? { confidence: neurons.confidence } : {}),
      ...(cite ? { source: { citation: cite } } : {}),
      // Drawn by the viewport from the neuron table and the skeletons; the app fetches no geometry.
      mesh: skeleton ? 'skeletons/' : 'neurons/',
      tract: null,
      ...(color ? { color } : {}),
    };
  };
  const centre = pack.column?.centre_body;
  // Proofread cells in the column, a layer at a time from the pia: round-robin over the layers.
  const byLayer = new Map<number, number[]>();
  for (const i of proofread)
    if (t.inColumn?.[i] === 1) {
      const l = t.layer?.[i] ?? 0;
      byLayer.set(l, [...(byLayer.get(l) ?? []), i]);
    }
  for (const list of byLayer.values()) list.sort((a, b) => Number(t.bodyId[b] === centre) - Number(t.bodyId[a] === centre) || typeOf[a]! - typeOf[b]! || t.bodyId[a]! - t.bodyId[b]!);
  const order = [...byLayer.keys()].sort((a, b) => (a === 0 ? 99 : a) - (b === 0 ? 99 : b));
  const total = [...byLayer.values()].reduce((n, l) => n + l.length, 0);
  const featured: number[] = [];
  for (let round = 0; featured.length < total; round++)
    for (const l of order) {
      const i = byLayer.get(l)![round];
      if (i !== undefined) featured.push(i);
    }
  const lead = featured.findIndex((i) => t.bodyId[i] === centre);
  if (lead > 0) featured.unshift(...featured.splice(lead, 1));
  const shown = new Set(featured);
  const kinds = new Set(neuropils.map((n) => n.kind ?? 'region'));
  const pils: Structure[] = neuropils.map((n) => ({
    id: `neuropil:${n.id}`,
    name: n.name,
    synonyms: [n.id],
    system: n.kind === 'shell' ? 'shell' : 'neuropil',
    parent: n.parent ? `neuropil:${n.parent}` : null,
    hemisphere: n.hemisphere ?? null,
    provenance: n.provenance,
    ...(n.layer && pack.layers?.[n.layer]?.method ? { method: pack.layers[n.layer]!.method } : {}),
    ...(n.layer && pack.layers?.[n.layer]?.confidence ? { confidence: pack.layers[n.layer]!.confidence } : {}),
    ...(cite ? { source: { citation: cite } } : {}),
    mesh: n.mesh ?? null,
    tract: null,
    ...(n.color ? { color: n.color } : {}),
  }));
  const structures = [...featured.map(cell), ...pils, ...proofread.filter((i) => !shown.has(i)).map(cell), ...column.map(cell)];
  const cat = index(pack, structures, 'connectome');
  return {
    ...cat,
    systems: cat.systems.map((g) => (g.system === 'neuropil' ? { ...g, label: neuropilLabel(kinds) } : g)),
  };
}

async function fetchOk(fetchFn: typeof fetch, url: string): Promise<Response> {
  const res = await fetchFn(url);
  if (!res.ok) throw new Error(`${url}: HTTP ${res.status}`);
  return res;
}

/** Load a connectome pack's catalogue from `packs/<id>/`: `neuropils.json`, `celltypes.json` and the neuron table's columns. */
export async function loadTissueCatalogue(base: string, pack: PackInfo, fetchFn: typeof fetch = fetch): Promise<Catalogue> {
  const json = async <T>(f: string) => (await (await fetchOk(fetchFn, base + f)).json()) as T;
  const [neuropils, celltypes, table] = await Promise.all([
    json<NeuropilRow[]>('neuropils.json'),
    json<CellTypes>('celltypes.json'),
    json<{ files: Record<string, { file: string }> }>('neurons/index.json'),
  ]);
  const column = async (name: string) => {
    const f = table.files[name]?.file;
    return f ? (await fetchOk(fetchFn, `${base}neurons/${f}`)).arrayBuffer() : null;
  };
  const [[bodyId, flags, layer, inColumn, skeleton], activity] = await Promise.all([
    Promise.all(['body_id', 'flags', 'layer', 'in_column', 'skeleton'].map(column)),
    // A pack's runs are optional to the catalogue: one that doesn't load leaves the cells named, and no run.
    loadActivity(base, fetchFn).catch(() => null),
  ]);
  if (!bodyId || !flags) throw new Error(`${base}neurons/index.json: no body_id or flags column`);
  const t: NeuronTable = {
    bodyId: new Uint32Array(bodyId),
    flags: new Uint8Array(flags),
    ...(layer ? { layer: new Uint8Array(layer) } : {}),
    ...(inColumn ? { inColumn: new Uint8Array(inColumn) } : {}),
    ...(skeleton ? { skeleton: new Uint8Array(skeleton) } : {}),
  };
  const cat = { ...tissueCatalogue(pack, neuropils, celltypes, t), tissue: { base, celltypes, table: t, files: table.files } };
  return activity ? { ...cat, activity } : cat;
}

/** What Methods says about a tissue pack: its source, licence and how each drawn layer was made. */
export interface TissueMethods {
  caption: string;
  text: string;
  citation?: string;
  licence?: { name: string; url?: string };
  /** The pack's layers that are on screen, as the sims' inputs are listed. */
  data: { what: string; provenance: string; method?: string }[];
  placement?: { space: string; provenance?: string; method?: string };
  /** The activity the viewport plays on the pack's neurons: the run, its run.json where it loaded, and
   *  what the producing lane asked Methods to keep about it. */
  run?: { id: string; title: string; info: RunInfo | null; note?: string };
}

/** Pack layers the viewport draws in a tissue view, with the names Methods gives them (activity while a run plays). */
const DRAWN: [string, string][] = [
  ['neurons', 'Cells and arbors'],
  ['celltypes', 'Cell types'],
  ['cortical-layers', 'Cortical layers'],
  ['vessels', 'Blood vessels'],
  ['activity', 'Activity'],
];

export function tissueMethods(pack: PackInfo, run?: TissueMethods['run'] | null): TissueMethods {
  const a = pack.attribution;
  const data = DRAWN.flatMap(([id, what]) => {
    const l = id === 'activity' && !run ? undefined : pack.layers?.[id];
    return l ? [{ what, provenance: l.provenance, ...(l.method ? { method: l.method } : {}) }] : [];
  });
  const title = a?.title ?? pack.id;
  // While a run plays, the caption says what's real and what's simulated: "H01 · Real wiring, simulated activity".
  const label = run ? (run.info?.label ?? 'Simulated activity') : null;
  return {
    caption: label ? `${title.split(':')[0]!.trim()} · ${label}` : title,
    text: a?.text ?? pack.id,
    ...(a?.citation ? { citation: a.citation } : {}),
    ...(pack.licence ? { licence: { name: pack.licence, ...(a?.licence_url ? { url: a.licence_url } : {}) } } : {}),
    data,
    ...(pack.placement ? { placement: { space: pack.placement.space, ...(pack.placement.provenance ? { provenance: pack.placement.provenance } : {}), ...(pack.placement.method ? { method: pack.placement.method } : {}) } } : {}),
    ...(run ? { run } : {}),
  };
}
